/**
 * Brand + site identity — the SINGLE source of truth for the public brand.
 *
 * This is the file the /rebrand skill rewrites (along with env vars, the
 * theme block in globals.css, messages copy, and public/ assets) to turn this
 * engine into a different site while keeping every feature identical.
 *
 * Rules:
 *  - NO import of `@/config/index` here — index.ts imports THIS file for its
 *    `app_*` defaults, so this module must stay dependency-free (it reads the
 *    same env vars itself).
 *  - Everything that varies per site lives in `siteConfig`; `agentIdentity`
 *    derives the machine-facing tokens (MCP namespace, bearer prefixes, UA
 *    strings, signature headers) from it. Modules and blocks must never
 *    hardcode a brand string — import from here instead.
 */

const metaEnv: Record<string, string | undefined> = (import.meta as any).env ?? {};
const procEnv: Record<string, string | undefined> =
  typeof process !== 'undefined' && process.env ? process.env : {};

const publicEnv = (key: string) => metaEnv[key] ?? procEnv[key];

const resolvedSiteUrl = (publicEnv('VITE_APP_URL') || 'http://localhost:3000').replace(/\/+$/, '');

const resolveSiteHost = (input: string) => {
  try {
    return new URL(input).hostname.replace(/^www\./, '');
  } catch {
    return 'localhost';
  }
};

/** One of our own products, cross-promoted from this directory. */
export type HousePromotion = {
  id: string;
  name: string;
  /** Landing URL — UTM params are appended per placement. */
  url: string;
  logo: string | null;
  /** Per-locale one-liner; `en` is the fallback. */
  description: { en: string } & Record<string, string>;
  /** The product's own listing slug here — its page never promotes itself. */
  listingSlug?: string;
  /**
   * Contextual targeting by taxonomy/item slug. Unset = rails and the
   * post-submit screen only (never a contextual card).
   */
  match?: {
    items?: readonly string[];
    categories?: readonly string[];
    groups?: readonly string[];
    collections?: readonly string[];
  };
};

export const siteConfig = {
  // ── Brand ───────────────────────────────────────────────────────────────
  name: 'Seek AI',
  tagline: 'Discover Your Next AI Tool',
  description: 'Seek AI is a curated directory of the best AI tools — every listing scored by independent AI agents on six dimensions, with weekly price checks and agent-readiness checks. Compare features, pricing, and alternatives to find the right tools for your workflow.',
  keywords: [
    'AI tools',
    'best AI tools',
    'AI tools directory',
    'AI tool alternatives',
    'AI agents',
    'AI image generator',
    'AI video generator',
    'AI coding assistant',
    'ChatGPT alternatives',
    'generative AI',
  ],
  author: 'SeekAI',
  url: resolvedSiteUrl,
  host: resolveSiteHost(resolvedSiteUrl),
  logo: publicEnv('VITE_APP_LOGO') ?? '/logo.png',
  image: `${resolvedSiteUrl}/og.png`,
  mail: 'contact@seekai.dev',
  links: {},
  utm: {
    source: resolveSiteHost(resolvedSiteUrl),
    medium: 'referral',
    campaign: 'navigation',
  },
  /**
   * Extra hosts (bare, no `www.`) whose editorial markdown links pass link
   * equity, on top of `host` itself and the owned hosts in
   * `owned-hosts.generated.ts`. The sites we own now come from the site
   * registry (`ops engines sync`); keep this empty.
   */
  dofollowHosts: [] as readonly string[],
  /**
   * Kill switch for the site-wide house promotions (rails, cards, banner):
   * true renders owned-host promotions as dofollow, false reverts them to
   * `rel="sponsored"`. Listing and markdown-body links are not affected.
   */
  housePromoDofollow: true,
  /**
   * Our own products, promoted in otherwise-empty placements (rail slots,
   * contextual item/category cards, the post-submit screen). The link `rel`
   * comes from `houseLinkRel()` (src/lib/owned-hosts.ts): dofollow for owned
   * hosts, `sponsored` otherwise. Empty = off.
   */
  housePromotions: [] as readonly HousePromotion[],
  /**
   * AI generator slugs served under /tools/<slug> (registry in
   * config/ai-tools). Empty = the /tools hub lists only site features.
   */
  aiTools: [] as readonly string[],

  // ── Agent channel identity ──────────────────────────────────────────────
  agent: {
    /** MCP/tool-name namespace: `{namespace}.get_project`, MCP server id, badge marker prefix. */
    namespace: 'seekai',
    /** Project token prefix: `{tokenPrefix}…` (e.g. sk_proj_…). */
    tokenPrefix: 'sk_proj_',
    /** Human-facing device/user code prefix: `{userCodePrefix}-XXXX-XXXX`. */
    userCodePrefix: 'SK',
    /** Publisher signature header prefix: `x-{namespace}-timestamp` etc. */
    headerPrefix: 'x-seekai-',
    /** Path to the agent skill file in public/. */
    skillPath: '/skill.md',
    /** System (enrich / admin import) user identity. */
    systemUser: {
      id: 'seekai-system-user',
      name: 'Seek AI System',
      email: 'system@seekai.dev',
    },
    /** Editorial system user name used for human-review placeholders. */
    editorialUserName: 'Seek AI Editorial',
    /** Alt text on the official footer badge. */
    badgeAlt: 'Featured on Seek AI',
  },
} as const;

export type SiteConfig = typeof siteConfig;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Derived machine-facing identity — everything an AI channel / bot needs.
 * Values are computed ONCE at module load from `siteConfig`, so a rebrand
 * only edits `siteConfig` above.
 */
export const agentIdentity = {
  /** MCP server id (`claude mcp add`, codex/cursor config keys). */
  mcpServerId: siteConfig.agent.namespace,
  /** Build a namespaced MCP tool name, e.g. toolName('get_project'). */
  toolName: (name: string) => `${siteConfig.agent.namespace}.${name}`,
  /** Validate a raw project token (no Bearer prefix). */
  projectTokenPattern: new RegExp(
    `^${escapeRegExp(siteConfig.agent.tokenPrefix)}[A-Za-z0-9_-]+$`,
  ),
  /** Parse `Authorization: Bearer sk_proj_…` (case-insensitive scheme). */
  projectBearerPattern: new RegExp(
    `^Bearer (${escapeRegExp(siteConfig.agent.tokenPrefix)}[A-Za-z0-9_-]+)$`,
    'i',
  ),
  /** Format a raw device/user code into its display shape, e.g. SK-ABCD-EFGH. */
  displayUserCode: (code: string) => `${siteConfig.agent.userCodePrefix}-${code}`,
  /** UA string, e.g. userAgent('Enrich') → SeekAI-Enrich/1.0 (+https://…). */
  userAgent: (sub: string, path = '') => `${siteConfig.author}-${sub}/1.0 (+${siteConfig.url}${path})`,
  /** Publisher (FastClaw) request-signature header names. */
  signatureHeaders: {
    timestamp: `${siteConfig.agent.headerPrefix}timestamp`,
    nonce: `${siteConfig.agent.headerPrefix}nonce`,
    signature: `${siteConfig.agent.headerPrefix}signature`,
  },
  /** Prefix for the redirect-follow marker meta injected during badge checks. */
  badgeMarkerPrefix: siteConfig.agent.namespace,
} as const;

export type AgentIdentity = typeof agentIdentity;
